import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import {
  createContext,
  useContext,
  // Fragment,
  ReactNode,
} from "react";
import { CloseCta } from "~/components/ctas";
import { NewsletterForm } from "~/components/shared";
import { useNavShowing } from "./NavShowingProvider";
import { useScrollLockedState } from "./ScrollLockProvider";

interface NewsletterContextType {
  openModal: () => void;
}

const NewsletterContext = createContext<NewsletterContextType | undefined>(
  undefined,
);

function NewsletterProvider({ children }: { children: ReactNode }) {
  const { setNavShowing } = useNavShowing();
  const [isOpen, setIsOpen] = useScrollLockedState(false);

  function closeModal() {
    setNavShowing(true);
    setIsOpen(false);
  }

  function openModal() {
    setNavShowing(false);
    setIsOpen(true);
  }

  return (
    <NewsletterContext.Provider
      value={{
        openModal,
      }}
    >
      <Dialog
        id="dialog"
        as="div"
        className="relative z-10"
        onClose={closeModal}
        open={isOpen}
      >
        <Transition show={isOpen} as={"div"}>
          <TransitionChild
            className="fixed inset-0"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="h-full w-full bg-black bg-opacity-70" />
          </TransitionChild>

          <TransitionChild
            className="lg:tbi-grid flex-center fixed inset-0 my-4 overflow-x-auto lg:my-0"
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="col-span-full col-start-1 grid h-full max-w-[345px] grid-cols-4 grid-rows-[min-content] overflow-x-hidden overflow-y-scroll bg-white text-black lg:col-span-6 lg:col-start-4 lg:h-min lg:max-w-full lg:grid-cols-6">
              <div className="flex-center col-span-1 col-start-4 my-0 aspect-square lg:static lg:col-start-6 lg:my-6">
                <CloseCta
                  kind="black"
                  onClick={closeModal}
                  className="aspect-square"
                />
              </div>
              <div
                className={clsx(
                  "col-span-4 col-start-1 row-start-2 mx-3 lg:col-start-2 lg:mx-0",
                )}
              >
                <NewsletterForm />
              </div>
            </DialogPanel>
          </TransitionChild>
        </Transition>
      </Dialog>
      {children}
    </NewsletterContext.Provider>
  );
}

function useNewsletter() {
  const context = useContext(NewsletterContext);
  if (context === undefined) {
    throw new Error("useNewsletter must be used within a NewsletterProvider");
  }
  return context;
}

export { NewsletterProvider, useNewsletter };
